<template>
    <div>
        <div v-if="isUser" class="boolean-selector">
            <button
                type="button"
                class="btn-choice btn-yes"
                :class="{active: localSelectedAnswer && localSelectedAnswer.answer }"
                @click="(e) => radioButtonClicked(true, e)"
            >
                <svg width="15" height="15" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
                Yes
            </button>

            <button
                type="button"
                class="btn-choice btn-no"
                :class="{active: localSelectedAnswer && !localSelectedAnswer.answer }"
                @click="(e) => radioButtonClicked(false, e)"
            >
                <svg width="15" height="15" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
                No
            </button>
        </div>

        <div v-if="isUser" class="mt-2">
            <button
                class="btn-saved"
                @click="onAnswerChanged"
                :disabled="booleanAnswer === null"
            >
                Save
            </button>
        </div>

        <div class="mt-2" v-if="isUser">
            <report-button
                questionText="Do you want to report an error?"
                callToActionText="Write us!"
                @click.native="showModal"
            />
        </div>

        <feedback-modal 
            :modalVisible.sync="modalVisible"
            :answers.sync="answers" 
            :treatmentHistory.sync="treatmentHistory"
            :excludedRecommendations.sync="excludedRecommendations"
            :branchId.sync="branchId"
        />

        <saving-warning-modal 
            type="bool"
            :warningModalVisible.sync="warningModalVisible" 
            :dependentQuestionNames.sync="dependentQuestionNames"
            :questionId="questionId"
            :booleanAnswer="booleanAnswer"
            :localGroups="localGroups"
            :localQuestions="localQuestions"
        />
        
        <div v-if="!isUser">
            <div class="custom-control custom-radio mr-3">
                <input :id="`BoolItemAnswer${questionId}-Yes`" :name="`answerForQuestion${questionId}`" :value="true" :disabled="isReadOnly"
                    class="custom-control-input" type="radio" required v-model="booleanAnswer" @click="(e) => radioButtonClicked(true,e)">
                <label class="custom-control-label" :for="`BoolItemAnswer${questionId}-Yes`">Yes</label>
            </div>
            <div class="custom-control custom-radio">
                <input :id="`BoolItemAnswer${questionId}-No`" :name="`answerForQuestion${questionId}`" :value="false" :disabled="isReadOnly"
                    class="custom-control-input" type="radio" required v-model="booleanAnswer" @click="(e) => radioButtonClicked(false,e)">
                <label class="custom-control-label" :for="`BoolItemAnswer${questionId}-No`">No</label>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import constants from "@/entities/constants";
import { BooleanAnswer } from "@/entities/questionaire/answers/BooleanAnswer";
import ReportButton from '../../UI/ReportButton.vue';
import FeedbackModal from '../../UI/FeedbackModal.vue';
import { Answer } from "@/entities/questionaire/answers/Answer";
import { Treatment } from "@/entities/Treatment";
import SavingWarningModal from "@/components/UI/SavingWarningModal.vue";
import { Property } from "@/entities/questionaire/Property";
import { Group } from "@/entities/questionaire/Group";

@Component({
    name: constants.answerTypes.BooleanAnswer.componentName,
    components: {
        ReportButton,
        FeedbackModal,
        SavingWarningModal,
    },
})
export default class AppBooleanAnswer extends Vue {
    public booleanAnswer: boolean | null | undefined = null;
    public localSelectedAnswer: BooleanAnswer | null = null;
    modalVisible: boolean = false;
    warningModalVisible: boolean = false;
    dependentQuestionNames: string[] = [];

    @Prop()
    questionId!: number;

    @Prop()
    answer!: BooleanAnswer;

    @Prop()
    selectedAnswer!: BooleanAnswer | null;

    @Prop()
    isReadOnly!: boolean;

    @Prop()
    isUser!: boolean;

    @Prop()
    answers!: Answer[];

    @Prop()
    treatmentHistory!: Treatment[] | null;

    @Prop()
    branchId!: number | null;

    @Prop()
    excludedRecommendations!: {
        id: number;
        name: string;
        approvalPercentage: number;
    }[] | [];

    @Prop()
    property!: Property;

    @Prop()
    public localGroups!: Group[];

    @Prop()
    localQuestions!: Property[];

    created() {
        this.localSelectedAnswer = this.selectedAnswer;

        if (this.selectedAnswer) {
            this.booleanAnswer = this.selectedAnswer.answer;
        }
    }

    radioButtonClicked(value: boolean, e: any) {
        if (this.booleanAnswer === value)
        {
            this.booleanAnswer = null;
            this.localSelectedAnswer =  null;
        } else {
            this.booleanAnswer = value;
            this.localSelectedAnswer = new BooleanAnswer(this.questionId, this.booleanAnswer, this.booleanAnswer ? "Yes" : "No");
        }
        e.target.blur();

        if (!this.isUser){
            this.onAnswerChanged();
        }
    }

    getAllDependentQuestionNames(id: number) {
        for (let i = 0; i < this.localQuestions.length; i++) {
            if (this.localQuestions[i].questionConditionId === id) {
                this.dependentQuestionNames.push(this.localQuestions[i].question);
                this.getAllDependentQuestionNames(this.localQuestions[i].id);
            }
        }
        return this.dependentQuestionNames;
    }

    checkDependentQuestion(question: Property) {
        const subDepQ = this.localQuestions.filter(q => q.questionConditionId === question.id);
        if (subDepQ.length > 0) {
            for (let i = 0; i < subDepQ.length; i++) {
                subDepQ[i].isHidden = true;
                this.checkDependentQuestion(subDepQ[i]);
            }
        }
    }

    onAnswerChanged() {
        const dependentQuestions = this.localQuestions
            .filter(question => question.questionConditionId === this.property.id)
            .map(question => question.id);

        if (dependentQuestions.length > 0) {
            const existingAnswer = this.answers.find(answer => answer.PropertyId === this.property.id);

            if (existingAnswer) {
                this.dependentQuestionNames = this.getAllDependentQuestionNames(this.property.id);
                this.warningModalVisible = true;
            } else {
                for (let j = 0; j < dependentQuestions.length; j++) {
                    const dependentQuestion = this.localQuestions.find(q => q.id === dependentQuestions[j]);

                    if (
                        (dependentQuestion?.answerQuestions?.includes(String(this.booleanAnswer)) && 
                        dependentQuestion.conditionQuestion === 'notIn') ||
                        (!dependentQuestion?.answerQuestions?.includes(String(this.booleanAnswer)) && 
                        dependentQuestion?.conditionQuestion === 'in')
                    ) {
                        dependentQuestion.isHidden = true;
                        this.checkDependentQuestion(dependentQuestion);
                    }
                }
                
                this.$events.emit("answerChanged", new BooleanAnswer(this.questionId, this.booleanAnswer, this.booleanAnswer ? "Yes" : "No"));
            }
        } else {
            this.$events.emit("answerChanged", new BooleanAnswer(this.questionId, this.booleanAnswer, this.booleanAnswer ? "Yes" : "No"));
        }
    }

    showModal() {
        this.modalVisible = true;
    }
}
</script>

<style lang="scss" scoped>

/* Boolean Selector Styles */
.boolean-selector {
    display: flex;
    flex-direction: row;
    gap: 0.625rem;
    margin-bottom: 0.75rem;
}

.btn-choice {
    min-width: 80px;
    height: 36px;
    padding: 0 1rem;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(15px);
    border-radius: 8px;
    border: 2px solid rgba(226, 232, 240, 0.8);
    color: #475569;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 600;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.btn-choice:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: rgba(102, 123, 242, 0.4);
}

.btn-choice:active {
    transform: translateY(0);
}

.btn-choice svg {
    transition: all 0.3s ease;
}

/* Yes Button Styles */
.btn-yes.active {
    background: linear-gradient(135deg, #10b981, #34d399);
    border-color: #10b981;
    color: white;
    box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.btn-yes.active:hover {
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

/* No Button Styles */
.btn-no.active {
    background: linear-gradient(135deg, #f25260, #ff6b7a);
    border-color: #f25260;
    color: white;
    box-shadow: 0 4px 15px rgba(242, 82, 96, 0.3);
}

.btn-no.active:hover {
    box-shadow: 0 6px 20px rgba(242, 82, 96, 0.4);
}

/* Action Buttons Container */
.action-btns {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-wrap: wrap;
}

/* Save Button Styles */
.btn-saved {
    background: white;
    color: #2f45a5;
    font-weight: 600;
    height: 36px;
    padding: 0 1rem;
    border-radius: 8px;
    border: 2px solid rgba(47, 69, 165, 0.35);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    transition: all 0.3s ease;
    cursor: pointer;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.btn-saved:not(:disabled):hover {
    background: linear-gradient(135deg, #2f45a5, #667bf2);
    border-color: transparent;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(47, 69, 165, 0.25);
}

.btn-saved:active:not(:disabled) {
    transform: translateY(0);
}

.btn-saved:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.btn-saved:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 123, 242, 0.2);
}

/* Radio Button Styles (for non-user view) */
input[type="radio"] {
    margin-top: calc(.375rem + 1px);
}

.custom-control-label {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    color: #475569;
}

/* Responsive Design */
@media only screen and (max-width: 599px) {
    .action-btns {
        flex-direction: column;
        align-items: stretch;
    }
}

@media only screen and (min-width: 600px) and (max-width: 767px) {
    .boolean-selector {
        flex-direction: row;
    }
    
    .action-btns {
        flex-direction: row;
        justify-content: space-between;
    }
}

@media only screen and (min-width: 768px) and (max-width: 991px) {
    .boolean-selector {
        flex-direction: row;
    }
    
    .action-btns {
        flex-direction: column;
        align-items: flex-start;
    }
}

@media only screen and (min-width: 992px) {
    .boolean-selector {
        flex-direction: row;
    }

    .action-btns {
        flex-direction: row;
        justify-content: flex-start;
    }

    .btn-choice {
        min-width: 150px;
        height: 52px;
        padding: 0 2rem;
        font-size: 1.05rem;
    }

    .btn-saved {
        height: 48px;
        padding: 0 2rem;
        font-size: 1.05rem;
    }
}
</style>
